import { useParams } from 'react-router-dom';
import classnames from 'classnames';
import {
  BlueRefundIcon,
  CheckboxTickIcon,
  ListActionsIcon,
  PrintIcon,
  ShareIcon,
  VerticalListActionsIcon
} from 'assets/icons';
import { CustomCheckbox, CustomPopover, If } from 'components';
import {
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectCurrentTeamRefundPermissions,
  selectUserDetails
} from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BoolFn, VoidFn, clevertapEvents } from 'types/baseTypes';
import { formatAmount, getAmountWithCurrency } from 'utils/amountUtils';
import { logAnalyticEvent } from 'utils/analytics';
import DateUtils from 'utils/dateUtils';
import { useLazyDownloadPrintReceiptAsPDFQuery } from '../api';
import { Receipt } from '../receipt.model';
import { ReceiptStatus } from '../receipts.model';
import { updateRefundAmount, updateRefundReason } from '../receiptsSlice';
import {
  getPayoutStatus,
  getPayoutStatusColor,
  getReceiptStatus,
  getReceiptStatusColor,
  shouldEnableRefund
} from '../util';
import { getDisputeStatusColor } from 'containers/disputes/utils';
import { PaymentSystem } from 'containers/payouts/constants';

interface ReceiptsTableProps {
  receiptsList: Receipt[];
  onToggleSelectAllLoadedReceipts: VoidFn;
  areAllLoadedReceiptsSelected: BoolFn;
  isSelectedReceipt: (receipt: Receipt) => string;
  onToggleReceiptSelection: (receipt: Receipt, isCheckedReceipt: boolean) => void;
  onReceiptClick: (receiptId: string) => void;
  onPayoutClick: (payoutId: string, receiptId: string, paymentSystem: PaymentSystem) => void;
  onRefundTransactionClick: (receiptId: string) => void;
  onDisputedTransactionClick: (disputeId: string, receiptId: string) => void;
  currentSelectedId: string;
  onShareReceiptClick: (invoiceId: string, receiptId: string) => void;
  onCreateRefundClick: (ReceiptId: string) => void;
}

const tableHeaders: Record<string, string> = {
  date: 'Date',
  invoiceNo: 'Receipt no.',
  customer: 'Customer',
  status: 'Status',
  amount: 'Amount',
  payout: 'Payout'
};

const isHiddenHeader = (header: string): boolean => {
  if (header === 'Customer' || header === 'Payout') return true;
  return false;
};

const ReceiptsTable: FC<ReceiptsTableProps> = ({
  receiptsList,
  onToggleSelectAllLoadedReceipts,
  areAllLoadedReceiptsSelected,
  onToggleReceiptSelection,
  isSelectedReceipt,
  onReceiptClick,
  onPayoutClick,
  onRefundTransactionClick,
  currentSelectedId,
  onShareReceiptClick,
  onCreateRefundClick,
  onDisputedTransactionClick
}) => {
  const [currentActionsPopoverId, setCurrentActionsPopoverId] = useState<string>(null);
  const [downloadPrintReceiptAsPDF] = useLazyDownloadPrintReceiptAsPDFQuery();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUserDetails);
  const currentRefundPermissions = useAppSelector(selectCurrentTeamRefundPermissions);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const currentTeam = useAppSelector(selectCurrentTeam);

  const { rootReceiptId } = useParams();

  const isShareEnabled = () => {
    return (
      customerPermissions.viewCustomersCreatedByTeam ||
      customerPermissions.editCustomersCreatedByTeam ||
      customerPermissions.editExistingCustomers
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="receipts-table-more-options" className="py-2">
        <ListActionsIcon
          className={twMerge(
            'hidden shrink-0 lg:flex',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
        <VerticalListActionsIcon
          className={twMerge(
            'mx-2 flex shrink-0 lg:hidden',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  const onToggleListItemActions = (receiptId: string) => {
    if (receiptId === currentActionsPopoverId) {
      setCurrentActionsPopoverId(null);
    } else {
      setCurrentActionsPopoverId(receiptId);
    }
  };

  const isListItemActionsVisible = (receiptId: string) => {
    return receiptId === currentActionsPopoverId;
  };

  const onCloseListItemActionsPopover = (receiptId: string) => {
    if (currentActionsPopoverId === receiptId) {
      setCurrentActionsPopoverId(null);
    }
  };

  const getReceiptAmount = (receipt: Receipt) => {
    if (+receipt.tipAmount > 0) {
      return getAmountWithCurrency(+receipt.payableAmount - +receipt.tipAmount);
    }
    return getAmountWithCurrency(receipt.payableAmount);
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<any> = ({
    id,
    invoiceId,
    enableRefund
  }: {
    id: string;
    invoiceId: string;
    isModifiable: boolean;
    isManualPayment: boolean;
    isAlreadyRefunded: boolean;
    enableRefund: boolean;
  }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        {isShareEnabled && (
          <div
            id="receipts-table-3dot-share-receipt"
            className={'flex flex-row items-center gap-2'}
            onClick={e => {
              onCloseListItemActionsPopover(id);
              e.stopPropagation();
              onShareReceiptClick(invoiceId, id);
              logAnalyticEvent(clevertapEvents.webReceiptsListMMShare, { 'Receipt Id': id });
            }}>
            <ShareIcon />
            <div>Share Receipt</div>
          </div>
        )}
        <div
          id="receipts-table-3dot-print-receipt"
          onClick={e => {
            e.stopPropagation();
            onCloseListItemActionsPopover(id);
            downloadPrintReceiptAsPDF({ receiptId: id });
            logAnalyticEvent(clevertapEvents.webReceiptsListMMPrint, { 'Receipt Id': id });
          }}
          className={'flex flex-row items-center gap-2'}>
          <PrintIcon />
          <div>Print</div>
        </div>
        {enableRefund && (
          <div
            id="receipts-table-3dot-refund-receipt"
            onClick={e => {
              e.stopPropagation();
              onCloseListItemActionsPopover(id);
              dispatch(updateRefundReason(''));
              dispatch(updateRefundAmount(''));
              onCreateRefundClick(id);
              logAnalyticEvent(clevertapEvents.webReceiptsListMMRefund, { 'Receipt Id': id });
            }}
            className={'flex flex-row items-center gap-2 '}>
            <BlueRefundIcon />
            <div>Refund</div>
          </div>
        )}
      </div>
    );
  };

  const hasChildrenRows = (receipt: Receipt) => {
    return receipt?.refundTransactions || receipt?.disputeTransactions || receipt?.returnTransactions;
  };

  const isCurrentRowSelected = (receipt: Receipt) => {
    return isSelectedReceipt(receipt) || currentSelectedId === receipt.id || rootReceiptId === receipt.id;
  };

  return (
    <table className="relative w-full table-auto">
      <thead className="sticky top-[-4px] z-30 border-borderGray bg-primary">
        <tr className="text-slateGrey border-b border-borderGray text-left text-xs lg:text-base [&>*]:py-5">
          <th>
            <CustomCheckbox
              id="select-all-receipts-checkbox"
              onClick={onToggleSelectAllLoadedReceipts}
              isChecked={areAllLoadedReceiptsSelected()}>
              {areAllLoadedReceiptsSelected() ? <CheckboxTickIcon /> : ''}
            </CustomCheckbox>
          </th>
          {Object.values(tableHeaders).map((header, index) => {
            return (
              <th
                className={`max-w-[120px] text-sbase font-semibold text-primaryText 
                ${isHiddenHeader(header) && 'hidden lg:flex'}`}
                key={header + index}>
                {header}
              </th>
            );
          })}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {receiptsList?.map((receipt, index) => (
          <React.Fragment key={`receipt-list-${index}-${receipt.id}`}>
            <tr
              key={receipt.id}
              id={`receipt-table-row-${index + 1}`}
              className={`remove-highlight cursor-pointer align-top [&>*]:px-2 [&>*]:py-5
              ${isCurrentRowSelected(receipt) ? 'bg-secondaryBtn' : ''} 
              ${hasChildrenRows(receipt) ? '' : 'border-b border-secondaryBtn'}`}
              onClick={() => {
                onReceiptClick(receipt.id);
                logAnalyticEvent(clevertapEvents.webReceiptsListDetails, { 'Receipt Id': receipt.id });
              }}>
              {/* checkbox */}
              <td className="w-8">
                <CustomCheckbox
                  id={`${receipt.id}-checkbox`}
                  onClick={() => onToggleReceiptSelection(receipt, Boolean(isSelectedReceipt(receipt)))}
                  isChecked={Boolean(isSelectedReceipt(receipt))}>
                  <If condition={Boolean(isSelectedReceipt(receipt))}>
                    <CheckboxTickIcon />
                  </If>
                </CustomCheckbox>
              </td>
              {/* createdAt */}
              <td>
                <div className="max-w-[120px] truncate text-sbase text-primaryText">
                  <div>
                    {DateUtils.getDateInFormat({ date: new Date(receipt.createdAt), dateFormat: 'dd MMM yyyy' })}
                  </div>
                </div>
              </td>
              {/* receipt number */}
              <td>
                <div className="max-w-[140px] text-sbase font-semibold text-primaryText">
                  <div className="truncate">{receipt.id}</div>
                </div>
              </td>
              {/* customer */}
              <td className="hidden lg:table-cell	">
                <div className="min-w-[80px] max-w-[120px]  text-sbase text-primaryText">
                  <div className="max-w-[140px] truncate">
                    {receipt.customerName || receipt.customerPhone || receipt.customerEmail || '_'}
                  </div>
                </div>
              </td>
              {/* receipt status */}
              <td>
                <div className="flex flex-col gap-2">
                  <div
                    className="min-w-[80px] max-w-[120px] text-sbase font-bold"
                    style={{
                      color: getReceiptStatusColor(receipt.status)
                    }}>
                    {getReceiptStatus(receipt.status)}
                  </div>
                </div>
              </td>
              {/* amount */}
              <td>
                <div className="flex flex-col gap-2">
                  <div
                    className="min-w-[80px] max-w-[120px]  truncate text-sbase font-semibold
                text-primaryText">
                    {/* Amount excluding tip*/}
                    {getReceiptAmount(receipt)}
                  </div>

                  <If condition={+receipt.tipAmount > 0}>
                    <div className="truncate text-px13 text-primaryText">{'+$' + formatAmount(receipt.tipAmount)}</div>
                  </If>
                </div>
              </td>
              {/* Payout */}
              <td className="hidden lg:flex">
                <div className="flex max-w-[120px] flex-col gap-2">
                  {receipt.payoutId ? (
                    <>
                      <div
                        className="truncate text-sbase font-semibold 
                    text-secondary underline decoration-secondary underline-offset-2"
                        onClick={e => {
                          e.stopPropagation();
                          onPayoutClick(receipt.payoutId, receipt.id, receipt.paymentSystem as PaymentSystem);
                        }}>
                        {receipt.payoutId}
                      </div>
                      <div
                        className="truncate text-px13 font-semibold"
                        style={{ color: getPayoutStatusColor(receipt.payoutStatus) }}>
                        {getPayoutStatus(receipt.payoutStatus)}
                      </div>
                    </>
                  ) : (
                    <div className="text-sbase text-primaryText ">NA</div>
                  )}
                </div>
              </td>
              {(receipt.status === ReceiptStatus.SUCCESS || receipt.status === ReceiptStatus.PENDING) && (
                <td
                  className="w-14 pr-2"
                  onClick={e => {
                    e.stopPropagation();
                  }}>
                  <CustomPopover
                    showArrow={true}
                    arrowOffset="right-6"
                    highlightAnchor={false}
                    anchorComponent={ListActionsAnchor}
                    zIndex="z-[101]"
                    offset="-right-6"
                    margin="mt-1"
                    onClosePopover={() => onCloseListItemActionsPopover(receipt.id)}
                    show={isListItemActionsVisible(receipt.id)}
                    onTogglePopover={() => onToggleListItemActions(receipt.id)}>
                    {
                      <ListItemActionsMenu
                        id={receipt.id}
                        invoiceId={receipt.invoiceId}
                        enableRefund={shouldEnableRefund(
                          receipt.isManualPayment,
                          currentRefundPermissions,
                          receipt.merchantId === currentUser.id,
                          receipt.refundTransactions && receipt.refundTransactions[0]?.status !== '',
                          receipt.paymentMethod,
                          receipt.isRefundExpired
                        )}
                      />
                    }
                  </CustomPopover>
                </td>
              )}
            </tr>
            {receipt.refundTransactions &&
              receipt.refundTransactions.map((refundTransaction, i) => {
                return (
                  <React.Fragment key={`refund-transaction-${i}-${refundTransaction.id}`}>
                    <tr>
                      <td colSpan={8} className="py-0.5"></td>
                    </tr>
                    <tr
                      key={refundTransaction.id}
                      id={`receipt-row-${index + 1}-refund-${i + 1}`}
                      className="remove-highlight cursor-pointer align-top [&>*]:p-2"
                      onClick={() => {
                        onRefundTransactionClick(refundTransaction.id);
                      }}>
                      {/* checkbox */}
                      <td className="w-8"></td>
                      {/* createdAt */}
                      <td className="rounded-l-md border-0 bg-secondaryBtn pl-4">
                        <div className="max-w-[120px] truncate text-sbase text-primaryText">
                          <div>
                            {DateUtils.getDateInFormat({
                              date: new Date(refundTransaction.createdAt),
                              dateFormat: 'dd MMM yyyy'
                            })}
                          </div>
                        </div>
                      </td>
                      {/* Transaction Id */}
                      <td className="bg-secondaryBtn">
                        <div className="max-w-[140px] text-sbase font-normal text-primaryText">
                          <div className="truncate">{refundTransaction.id}</div>
                        </div>
                      </td>
                      {/* customer */}
                      <td className="hidden bg-secondaryBtn lg:table-cell">
                        <div className="min-w-[80px] max-w-[120px]  text-sbase text-primaryText">
                          <div className="max-w-[140px] truncate">
                            {receipt.customerName || receipt.customerPhone || receipt.customerEmail || '_'}
                          </div>
                        </div>
                      </td>
                      {/* status */}
                      <td className="bg-secondaryBtn">
                        <div className="flex flex-col gap-2">
                          <div
                            className="min-w-[80px] max-w-[120px] text-sbase font-bold"
                            style={{
                              color: getReceiptStatusColor(refundTransaction.status)
                            }}>
                            {getReceiptStatus(refundTransaction.status)}
                          </div>
                        </div>
                      </td>
                      {/* amount */}
                      <td className="bg-secondaryBtn">
                        <div className="flex flex-col gap-2">
                          <div
                            className="min-w-[80px] max-w-[120px]  truncate text-sbase font-semibold
                                 text-primaryText">
                            {'$' + formatAmount(refundTransaction.amount)}
                          </div>
                        </div>
                      </td>
                      <td className="bg-secondaryBtn">
                        <div className=""></div>
                      </td>
                      <td className="w-14 rounded-r-md  border-0 bg-secondaryBtn pr-2"></td>
                    </tr>
                  </React.Fragment>
                );
              })}
            {receipt.disputeTransactions &&
              receipt.disputeTransactions.map((disputeTransaction, i) => {
                return (
                  <React.Fragment key={`dispute-transaction-${i}-${disputeTransaction.disputeId}`}>
                    <tr>
                      <td colSpan={8} className="py-0.5"></td>
                    </tr>
                    <tr
                      key={disputeTransaction.disputeId}
                      id={`receipt-row-${index + 1}-refund-${i + 1}`}
                      className="remove-highlight cursor-pointer align-top [&>*]:p-2"
                      onClick={() => {
                        onDisputedTransactionClick(disputeTransaction.disputeId, receipt.id);
                      }}>
                      {/* checkbox */}
                      <td className="w-8"></td>
                      {/* createdAt */}
                      <td className="rounded-l-md border-0 bg-[#FBF1DF] pl-4">
                        <div className="max-w-[120px] truncate text-sbase text-primaryText">
                          <div>
                            {DateUtils.getDateInFormat({
                              date: new Date(disputeTransaction.createdAt),
                              dateFormat: 'dd MMM yyyy'
                            })}
                          </div>
                        </div>
                      </td>
                      {/* dispute id*/}
                      <td className="bg-[#FBF1DF]">
                        <div className="max-w-[140px] text-sbase font-normal text-primaryText">
                          <div className="truncate">{disputeTransaction.disputeId}</div>
                        </div>
                      </td>
                      {/* customer */}
                      <td className="hidden bg-[#FBF1DF] lg:table-cell">
                        <div className="min-w-[80px] max-w-[120px]  text-sbase text-primaryText">
                          <div className="max-w-[140px] truncate">
                            {receipt.customerName || receipt.customerPhone || receipt.customerEmail || '_'}
                          </div>
                        </div>
                      </td>
                      {/* dispute status */}
                      <td className="bg-[#FBF1DF]">
                        <div className="flex flex-col gap-2">
                          <div
                            className="min-w-[80px] max-w-[135px] text-sbase font-bold"
                            style={{ color: getDisputeStatusColor(disputeTransaction.status) }}>
                            {getReceiptStatus(disputeTransaction.status)}
                          </div>
                        </div>
                      </td>
                      {/* amount */}
                      <td className="bg-[#FBF1DF]">
                        <div className="flex flex-col gap-2">
                          <div
                            className="min-w-[80px] max-w-[120px]  truncate text-sbase font-semibold
                                 text-primaryText">
                            {`$${disputeTransaction.disputeAmount}`}
                          </div>
                        </div>
                      </td>
                      <td className="bg-[#FBF1DF]">
                        <div className=""></div>
                      </td>
                      <td className="w-14 rounded-r-md  border-0 bg-[#FBF1DF] pr-2"></td>
                    </tr>
                  </React.Fragment>
                );
              })}
            {receipt.returnTransactions &&
              receipt.returnTransactions.map((returnTransaction, i) => {
                return (
                  <React.Fragment key={`return-transaction-${i}-${returnTransaction.id}`}>
                    <tr
                      key={returnTransaction.id}
                      id={`receipt-row-${index + 1}-return-${i + 1}`}
                      className="remove-highlight cursor-pointer align-top [&>*]:p-2"
                      onClick={() => {
                        onReceiptClick(returnTransaction.id);
                      }}>
                      {/* checkbox */}
                      <td className="w-8"></td>
                      {/* createdAt */}
                      <td className="rounded-l-md border-0 bg-secondaryBtn pl-4">
                        <div className="max-w-[120px] truncate text-sbase text-primaryText">
                          <div>
                            {DateUtils.getDateInFormat({
                              date: new Date(returnTransaction.createdAt),
                              dateFormat: 'dd MMM yyyy'
                            })}
                          </div>
                        </div>
                      </td>
                      {/* return id*/}
                      <td className="bg-secondaryBtn">
                        <div className="max-w-[140px] text-sbase font-normal text-primaryText">
                          <div className="truncate">{returnTransaction.id}</div>
                        </div>
                      </td>
                      {/* customer */}
                      <td className="hidden bg-secondaryBtn lg:table-cell">
                        <div className="min-w-[80px] max-w-[120px]  text-sbase text-primaryText">
                          <div className="max-w-[140px] truncate">
                            {receipt.customerName || receipt.customerPhone || receipt.customerEmail || '_'}
                          </div>
                        </div>
                      </td>
                      {/* return status */}
                      <td className="bg-secondaryBtn">
                        <div className="flex flex-col gap-2">
                          <div
                            className="min-w-[80px] max-w-[135px] text-sbase font-bold"
                            style={{ color: getReceiptStatusColor(returnTransaction.status) }}>
                            {getReceiptStatus(returnTransaction.status)}
                          </div>
                        </div>
                      </td>
                      {/* amount */}
                      <td className="bg-secondaryBtn">
                        <div className="flex flex-col gap-2">
                          <div
                            className="min-w-[80px] max-w-[120px]  truncate text-sbase font-semibold
                                 text-primaryText">
                            {'$' + formatAmount(returnTransaction.amount)}
                          </div>
                        </div>
                      </td>
                      <td className="bg-secondaryBtn">
                        <div className=""></div>
                      </td>
                      <td className="w-14 rounded-r-md  border-0 bg-secondaryBtn pr-2"></td>
                    </tr>
                  </React.Fragment>
                );
              })}
            {hasChildrenRows(receipt) && (
              <tr>
                <td colSpan={8} className="border-b border-secondaryBtn pt-4 "></td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

export default ReceiptsTable;
